import { eod } from '../classes';

export default class MeasurementType {
    static Metric_DataTypes = [{ value: 'BOOLEAN', text: 'Boolean' }, { value: 'FLOAT', text: 'Float' }, { value: 'STRING', text: 'String' }];
    static Metric_Methods = [{ value: 'MONITORED', text: 'Monitored', info: 'Abonneer u op de waarde op de IoT gateway en ontvang de waarde ervan wanneer deze is gewijzigd. Dit wordt meestal gebruikt voor booleaanse gegevens (zoals klepposities) of stringgegevens (zoals batchnummers)' }, { value: 'POLLED', text: 'Polled', 'info': 'verzamel de waarde met regelmatige tussenpozen, bijvoorbeeld elke seconde. Het resulterende datapunt krijgt de tijd waarop de peiling is gestart' }];

    static searchConfig = [{
        'icon': 'mdi-format-text',
        'label': 'Naam',
        'type': 'property',
        'settings': {
            'fields': ['name'],
            'title': '{name}',
        },
        'filter': 'name'
    },
    {
        'icon': 'mdi-form-select',
        'label': 'Asset type',
        'type': 'relation',
        'settings': {
            'module': 'productTypes',
            'id': 'productTypeId',
            'fields': ['name'],
            'title': '{name}',
            'options': {
                enabled: true
            }
        },
        'filter': 'productTypes_measurementTypes'
    }];
    static exportFields = ['id', 'name', 'description', 'unit{id}', 'isWritable', 'field', 'metricConfig{hardwareId dataType factor offset interval method}', 'valueConfigList{name}', 'formConfig{enabled required photo photoRequired photoMax comment commentRequired type category subCategory defaultValue values}'];
    static moduleName = 'measurementTypes';
    // Velden die ListInput/ListItemInput in de backend accepteren: al de rest (bv. het
    // meegefetchte measurementTypes{id} voor de gedeelde-lijst-waarschuwing) moet uit de
    // payload gefilterd worden, anders weigert GraphQL de input.
    static LIST_INPUT_FIELDS = ['id', 'name', 'description', 'itemType', 'fields', 'isActive', 'isDeleted', 'isSystem', 'externalIds'];
    static LIST_ITEM_INPUT_FIELDS = ['id', 'name', 'description', 'fields', 'isActive', 'isDefault', 'op', 'low', 'high', 'threshold', 'stepSize', 'color', 'externalIds', 'isDeleted'];

    constructor(item) {
        if (item) {
            for (const key in item) {
                if (item.hasOwnProperty(key)) {
                    this[key] = item[key];
                }
            }
        }
    }
    getListItems() {
        if (this.valueConfigListItems && this.valueConfigListItems.length > 0) {
            return this.valueConfigListItems;
        } else if (this.valueConfigList && this.valueConfigList.listItems && this.valueConfigList.listItems.length > 0) {
            return this.valueConfigList.listItems;
        } else if (this.formConfig && this.formConfig.type == 'yesno') {
            return [
                {
                    threshold: 0,
                    name: 'Nee'
                },
                {
                    threshold: 1,
                    name: 'Ja'
                }
            ];
        }

        return [];
    }
    getListItemsByThreshold() {
        let itemsByThreshold = {};
        let listItems = this.getListItems();

        for (let i = 0; i < listItems.length; i++) {
            const listItem = listItems[i];
            itemsByThreshold[listItem.threshold] = listItem;
        }

        return itemsByThreshold;
    }
    // persist === true  -> echte save: sub-entiteiten (templates, lijst, lijstitems)
    //                      worden via de API bewaard en de payload verwijst er via id naar.
    // persist === false  -> pure serialisatie voor dirty-detectie/snapshotting: geen
    //                      API-calls en het live-item (this) wordt niet gemuteerd.
    async getSaveData(persist = true) {
        let data = Object.assign({}, this);

        if (data.hasOwnProperty('templates')) {
            const templates = [];
            if (this.templates) {
                for (let i = 0; i < this.templates.length; i++) {
                    let template = this.templates[i];
                    let id = template.id;

                    if (persist) {
                        id = await eod.save('Template', template).then(result => {
                            if (result.data.data.createTemplate) {
                                return result.data.data.createTemplate.id;
                            } else {
                                return result.data.data.updateTemplate.id;
                            }
                        });

                        this.templates[i].id = id;
                    }

                    templates.push({
                        id: id,
                        default: template.default ? template.default : false
                    });
                }
            }
            data.templates = templates;
        }

        // De lijst gaat genest mee in de MeasurementTypeInput: de backend maakt de lijst
        // en de items zonder id aan en werkt die mét id bij, in één transactie. Pure
        // serialisatie (geen API-calls), dus identiek voor save en dirty-detectie.
        if (data.hasOwnProperty('valueConfigList')) {
            if (!data.valueConfigList) {
                data.valueConfigListId = null;   // loskoppelen; de lijst zelf blijft bestaan
                delete data.valueConfigList;
            } else {
                const pick = (obj, keys) => keys.reduce((acc, key) => {
                    if (obj[key] !== undefined) {
                        acc[key] = obj[key];
                    }
                    return acc;
                }, {});

                const valueConfigList = pick(data.valueConfigList, MeasurementType.LIST_INPUT_FIELDS);

                if (!valueConfigList.name) {
                    valueConfigList.name = data.name;
                }

                valueConfigList.listItems = (data.valueConfigList.listItems || [])
                    .filter(listItem => listItem.name && !isNaN(listItem.threshold))
                    .map(listItem => pick(listItem, MeasurementType.LIST_ITEM_INPUT_FIELDS));

                data.valueConfigList = valueConfigList;
                delete data.valueConfigListId;   // het geneste object is de bron van waarheid
            }
        }

        if (data.hasOwnProperty('metricConfig')) {
            const floats = ['factor', 'offset', 'interval'];
            if (data.metricConfig) {
                for (let i = 0; i < floats.length; i++) {
                    const propName = floats[i];
                    if (data.metricConfig[propName] != null) {
                        data.metricConfig[propName] = parseFloat(data.metricConfig[propName]);
                    }
                }
            }
        }

        if (data.hasOwnProperty('productTypes') && data.productTypes) {
            // Nieuwe array i.p.v. de gedeelde array-referentie muteren, zodat het
            // live-item zijn productType-namen behoudt bij dirty-detectie.
            data.productTypes = data.productTypes.map(productType => ({
                id: productType.id,
                settings: productType.settings?.map(item => ({ ...item, value: parseFloat(item.value) }))
            }));
        }

        if (data.hasOwnProperty('unit')) {
            data.unitId = data.unit ? data.unit.id : null;
        }
        delete data.unit;

        // Afgeleid meetwaardetype (eodprocessor#170): bron en instellingstype gaan als id mee,
        // de factor als getal (standaard 1).
        if (data.hasOwnProperty('sourceMeasurementType')) {
            data.sourceMeasurementTypeId = data.sourceMeasurementType ? data.sourceMeasurementType.id : null;
        }
        delete data.sourceMeasurementType;
        if (data.hasOwnProperty('settingMeasurementType')) {
            data.settingMeasurementTypeId = data.settingMeasurementType ? data.settingMeasurementType.id : null;
        }
        delete data.settingMeasurementType;
        if (data.hasOwnProperty('factor')) {
            const factor = parseFloat(data.factor);
            data.factor = isNaN(factor) || factor === 0 ? 1 : factor;
        }

        return data;
    }
};